import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';

import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SidebarLinkWorkflows} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ViewWorkflowHistory} from 'src/app/workflows/ViewWorkflowHistory';
import {
  WorkflowDocumentTitle,
  WorkflowPillConfig,
} from 'src/app/workflows/workflowHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetWorkflowDefinitionQuery} from 'src/redux/queries/workflowDefinition';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import type {IFormData} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Workflow',
  entityNamePlural: 'Workflows',
  listPath: PathUtility.WorkflowsDashboard('manage'),
  viewPath: id => PathUtility.WorkflowViewDefinition(String(id)),
};

const meta: IFormFieldV2[] = [
  {
    name: 'creatorName',
    label: 'Created By',
    type: 'text',
    required: true,
  },
  {
    name: 'createdAt',
    label: 'Created At',
    type: 'date',
    required: true,
  },
  {
    name: 'updatedAt',
    label: 'Last Updated At',
    type: 'date',
    required: true,
  },
];

export const ViewWorkflowDefinitionPage = () => {
  useSetDocumentTitle(WorkflowDocumentTitle);
  const {id} = useParams();
  const navigate = useAppNavigate();

  const perms = useModulePermissions(ModuleEnum.Workflows);

  const parsedId = id ? parseInt(id) : -1;

  const {data, isLoading} = useGetWorkflowDefinitionQuery(parsedId, {
    skip: id === undefined,
  });

  const formData: IFormDataV2 = useMemo(() => {
    if (!data) return {};
    return {
      ...(data as IFormData),
      creatorName: data.creator?.name,
    };
  }, [data]);

  const handleEdit = () => navigate(PathUtility.WorkflowEdit(id as string));

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode: 'view',
        id,
        itemName: data?.name,
      }),
    [id, data?.name]
  );

  const pills = useMemo(
    () => WorkflowPillConfig(data?.active ?? false),
    [data?.active]
  );

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Workflows}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Workflows}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkWorkflows.icon}
            subtitle={data?.internalIdentifier}>
            {data?.name ?? 'Workflow not found'}
          </PageHeader.Title>

          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>

          <PageHeader.Actions>
            {perms.update && (
              <PageHeader.Action variant="edit" onClick={handleEdit}>
                Edit
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <FormViewComponentV2 formData={formData} formMeta={meta} />

        <Divider />

        <ViewWorkflowHistory definitionId={id} />
      </Stack>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.WorkflowViewDefinition(':id'),
  element: <ViewWorkflowDefinitionPage />,
  module: ModuleEnum.Workflows,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default ViewWorkflowDefinitionPage;
